import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { OrganizationRepository } from "app/organization/organization.repository";
import { Insurance } from "app/quote/models/insurance.model";
import { Quote } from "app/quote/quote.type";
import {
  currencies,
  currenciesList,
} from "app/shared/currencies/currencies.const";
import { User } from "app/user/user.model";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Chip } from "primeng/chip";
import { Popover } from "primeng/popover";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";
import { InputText } from "primeng/inputtext";
import { Select } from "primeng/select";
import { Tooltip } from "primeng/tooltip";
import { CurrencyPipe } from "../../../shared/currencies/currency.pipe";

@Component({
  selector: "app-quote-insurance",
  templateUrl: "./quote-insurance.component.html",
  styleUrls: ["./quote-insurance.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    Chip,
    Popover,
    PopoverArrowDirective,
    Checkbox,
    FormsModule,
    InputText,
    Select,
    Tooltip,
  ],
})
export class QuoteInsuranceComponent implements OnInit, OnChanges {
  private organizationRepository = inject(OrganizationRepository);
  private currencyPipe = new CurrencyPipe();

  @Input() quote: Quote;
  readonly disabled = input<boolean>(false);
  @Input() user: User;
  // The control sits on the quote header and in the Layout tab at the same time.
  // Each host styles its own option rows, so the trigger has to wear the class
  // that host's stylesheet reaches for.
  readonly variant = input<"header" | "layout">("header");

  readonly isHeaderVariant = computed(() => this.variant() === "header");
  readonly checkboxId = computed(() => `show-insurance-${this.variant()}`);

  readonly apply = output<{
    insurance: {
      showInsurance: boolean;
      insuranceCargoValue: number;
      insuranceFreightValue: number;
      insuranceRatePercent: number;
      insuranceCurrency: string;
    };
  }>();

  public insurance: Insurance;
  public currencySelectList = [];
  public insuranceSubject = new Subject<Insurance>();
  public insuranceMinValue: number;
  public insuranceMinCurrency: string;
  protected subscription = new Subscription();

  ngOnInit() {
    this.insurance = this.quote.getInsurance();
    this.buildCurrencyList();
    this.insuranceSubject.pipe(debounceTime(300)).subscribe((item) => {
      this.applyEmit();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.quote && this.insurance) {
      this.quote = changes.quote.currentValue;
      this.insurance = this.quote.getInsurance();
    }
    if (!!changes.user && this.insurance) {
      this.user = changes.user.currentValue;
      this.insuranceMinValue = this.user
        .getTeam()
        .getOrganization()
        .getInsuranceMinValue();
      this.insuranceMinCurrency = this.user
        .getTeam()
        .getOrganization()
        .getInsuranceMinCurrency();
    }
  }

  public calculateInsurance() {
    if (this.insurance && this.insurance.showInsurance) {
      return this.insurance.insuranceTotal;
    }

    return 0;
  }

  public isInsured(): boolean {
    return !!this.insurance?.showInsurance;
  }

  public insuranceLabel(): string {
    const total = this.currencyPipe.transform(this.calculateInsurance());
    const label = `${total} ${this.insurance.insuranceCurrency}`;

    if (
      this.insuranceMinValue === null ||
      this.insuranceMinValue === undefined
    ) {
      return label;
    }

    const min = this.currencyPipe.transform(this.insuranceMinValue);
    const minCurrency = this.insuranceMinCurrency
      ? ` ${this.insuranceMinCurrency}`
      : "";

    return `${label} (min: ${min}${minCurrency})`;
  }

  public applyInsurance(panel: Popover): void {
    if (!this.insurance.showInsurance) {
      this.insurance.showInsurance = true;
      this.insuranceSubject.next(this.insurance);
    }

    panel.hide();
  }

  public togglePanel(event: Event, panel: Popover, target?: HTMLElement): void {
    panel.toggle(event, target);
  }

  public enableInsurance(
    event: Event,
    panel: Popover,
    target?: HTMLElement,
  ): void {
    if (!this.insurance.showInsurance) {
      this.insurance.showInsurance = true;
      this.insuranceSubject.next(this.insurance);
    }

    panel.show(event, target);
  }

  public onShowInsuranceChange(
    checked: boolean,
    event: Event,
    panel: Popover,
    target?: HTMLElement,
  ): void {
    if (checked) {
      this.enableInsurance(event, panel, target);

      return;
    }

    this.removeInsurance(panel);
  }

  public removeInsurance(panel: Popover): void {
    this.insurance.showInsurance = false;
    this.insuranceSubject.next(this.insurance);
    panel.hide();
  }

  public applyEmit() {
    this.apply.emit({
      insurance: {
        showInsurance: this.insurance.showInsurance,
        insuranceCargoValue: this.insurance.insuranceCargoValue,
        insuranceFreightValue: this.insurance.insuranceFreightValue,
        insuranceRatePercent: this.insurance.insuranceRatePercent,
        insuranceCurrency: this.insurance.insuranceCurrency,
      },
    });
  }

  private buildCurrencyList(): void {
    currenciesList.forEach((currKey) => {
      this.currencySelectList.push({
        label: currencies[currKey].value,
        value: currencies[currKey].value,
      });
    });
  }
}
